import { EcoaInput } from "@components/common/EcoaInput";
import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { CrfTextItemSpec } from "maven-lib/dist/consts/edc";
import { convertToTextFormat, handleValidateTextItemInputByNumberType, validateTextFormat } from "maven-lib/dist/consts/edc/lib";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

interface QuestionnaireTextItemProps {
  questionnaire: EcoaQuestionnaire;
  item: CrfTextItemSpec;
  disabled?: boolean;
  isAvailable?: boolean;
  defaultItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  onValueChange: (values: Array<{ itemId: string | null; dbVal: string | number | null }>) => void;
}

interface StyledTextWrapperProps {
  textAlignment?: "LEFT" | "CENTER";
}

const StyledTextWrapper = styled.div<StyledTextWrapperProps>`
  & > div.hb-text > input {
    text-align: ${(props) => props.textAlignment?.toLowerCase() || "left"};
  }
`;

export function QuestionnaireTextItem({
  item,
  isAvailable = true,
  disabled,
  defaultItemValues,
  onValueChange,
}: QuestionnaireTextItemProps) {
  const { uiPrefix, uiSuffix, textAlignment } = item;
  const dbVal = defaultItemValues?.find((val) => val.itemId === item.id)?.dbVal || null;

  const handleValueChange = (value: number | string | null, ev?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (ev && item?.format) {
      const convertedValue = convertToTextFormat(ev.currentTarget.value, item.format);
      const [isCorrected] = validateTextFormat(convertedValue, { ...item.format, disableQuery: undefined });

      const isValidateValue = (() => {
        if (item.format.type === "NATURAL_NUMBER" || item.format.type === "DECIMAL") {
          if (!item.format.disableCleanup) {
            const isPositive = (() => {
              if ("maxval" in item.format && Number(item.format.maxval) < 0) {
                return false;
              }

              if ("minval" in item.format && Number(item.format.minval) >= 0) {
                return true;
              }

              return false;
            })();

            return handleValidateTextItemInputByNumberType(
              item.format.type === "NATURAL_NUMBER" ? "NATURAL_NUMBER" : "DECIMAL",
              convertedValue,
              isPositive
            );
          }
        }
        return true;
      })();

      if (!isValidateValue && !isCorrected && value !== "") {
        return;
      }

      return onValueChange([{ itemId: item.id, dbVal: convertedValue }]);
    }
    onValueChange([{ itemId: item.id, dbVal: value }]);
  };

  useEffect(() => {
    if (!isAvailable && defaultItemValues?.length && dbVal !== null && !item.ecoa?.noSkipWhenDisabled) {
      onValueChange([{ itemId: item.id, dbVal: null }]);
    }
  }, [dbVal, defaultItemValues?.length, isAvailable, item.id, onValueChange, item.ecoa?.noSkipWhenDisabled]);

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (item.format?.type === "NATURAL_NUMBER" || item.format?.type === "DECIMAL") {
      if (item.format.disableCleanup) {
        return;
      }

      const numericValue = Number(e.target.value);

      if (!isNaN(numericValue)) {
        if (numericValue < Number(item.format.minval)) {
          onValueChange([{ itemId: item.id, dbVal: null }]);
        }

        if (numericValue > Number(item.format.maxval)) {
          onValueChange([{ itemId: item.id, dbVal: null }]);
        }
      }
    }
  };

  return (
    <StyledTextWrapper textAlignment={textAlignment}>
      <EcoaInput
        disabled={disabled}
        placeholder={item.placeholder ?? MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_ENTER_TEXT)}
        multiline={item.appearance === "MULTILINE"}
        defaultVal={dbVal}
        prefix={typeof uiPrefix === "string" ? uiPrefix : undefined} // NOTE: 20231013 prefix에서 site code나 item ref 조건은 필요한 경우 구현하기로 함
        suffix={typeof uiSuffix === "string" ? uiSuffix : undefined}
        onValueChange={handleValueChange}
        onBlur={handleInputBlur}
      />
    </StyledTextWrapper>
  );
}
